import { useMemo, useState } from 'react';
import { MessageSquareText, Send } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { formatarTelefone } from '@/utils/formatters';
import { enviarSmsManualLead } from '@/lib/leadSmsApi';
import { useQueryClient } from '@tanstack/react-query';

const CAMPOS_TELEFONE = [
  'telefone',
  'contato_real_telefone',
  'tel_sigma1',
  'tel_sigma2',
  'telefone_adm',
  'telefone_2',
  'telefone_3',
  'telefone_4',
] as const;

function normalizarCelular(valor: unknown): string | null {
  let digitos = String(valor || '').replace(/\D/g, '');
  if ((digitos.length === 12 || digitos.length === 13) && digitos.startsWith('55')) digitos = digitos.slice(2);
  if (digitos.length !== 11 || digitos[2] !== '9') return null;
  return digitos;
}

interface LeadSmsDialogProps {
  lead: Record<string, unknown> & { id: string };
}

export function LeadSmsDialog({ lead }: LeadSmsDialogProps) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [telefone, setTelefone] = useState('');
  const [mensagem, setMensagem] = useState('');
  const [enviando, setEnviando] = useState(false);

  const telefones = useMemo(() => Array.from(new Set(
    CAMPOS_TELEFONE
      .map((campo) => normalizarCelular(lead[campo]))
      .filter((item): item is string => Boolean(item)),
  )), [lead]);

  const enviar = async () => {
    if (!telefone) return toast.error('Escolha um celular do lead.');
    const texto = mensagem.trim();
    if (!texto) return toast.error('Digite a mensagem do SMS.');
    if (texto.length > 160) return toast.error('O SMS não pode passar de 160 caracteres.');

    setEnviando(true);
    try {
      await enviarSmsManualLead(lead.id, telefone, texto);
      toast.success('SMS enviado', { description: `${texto.length}/160 caracteres · ${formatarTelefone(telefone)}` });
      setMensagem('');
      setOpen(false);
      queryClient.invalidateQueries({ queryKey: ['lead-detail-modal', lead.id] });
      queryClient.invalidateQueries({ queryKey: ['lead-historico', lead.id] });
      queryClient.invalidateQueries({ queryKey: ['leads-prospeccao-server'] });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'O SMS não foi enviado.');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          disabled={telefones.length === 0}
          className="h-8 w-8 rounded-lg border border-white/[0.16] bg-white/[0.10] text-white/90 hover:bg-white/[0.18] hover:text-white disabled:border-white/[0.08] disabled:bg-white/[0.04] disabled:text-white/30"
          title={telefones.length ? 'Enviar SMS' : 'Nenhum celular válido neste lead'}
        >
          <MessageSquareText className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md overflow-hidden p-0">
        <DialogHeader className="bg-primary px-5 py-4 text-primary-foreground">
          <DialogTitle className="flex items-center gap-2 text-base">
            <MessageSquareText className="h-4 w-4" /> Enviar SMS
          </DialogTitle>
          <DialogDescription className="text-primary-foreground/70">
            Envio manual e individual pelo lead. Respostas por SMS não são recebidas pelo CRM.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 px-5 py-4">
          <div className="space-y-1.5">
            <Label htmlFor="lead-sms-numero" className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Celular</Label>
            <Select value={telefone} onValueChange={setTelefone}>
              <SelectTrigger id="lead-sms-numero" className="h-9 text-sm">
                <SelectValue placeholder="Escolha um número" />
              </SelectTrigger>
              <SelectContent>
                {telefones.map((numero) => (
                  <SelectItem key={numero} value={numero}>{formatarTelefone(numero)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between gap-3">
              <Label htmlFor="lead-sms-texto" className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Mensagem</Label>
              <span className={`text-[11px] tabular-nums ${mensagem.length >= 150 ? 'font-bold text-destructive' : 'text-muted-foreground'}`}>
                {mensagem.length}/160
              </span>
            </div>
            <Textarea
              id="lead-sms-texto"
              value={mensagem}
              onChange={(event) => setMensagem(event.target.value.slice(0, 160))}
              maxLength={160}
              rows={5}
              placeholder="Digite uma mensagem objetiva"
              className="resize-none text-sm"
            />
          </div>
        </div>
        <DialogFooter className="border-t bg-muted/30 px-5 py-3">
          <Button type="button" variant="outline" size="sm" onClick={() => setOpen(false)}>Cancelar</Button>
          <Button type="button" size="sm" onClick={enviar} disabled={enviando || !telefone || !mensagem.trim()}>
            <Send className="mr-1.5 h-3.5 w-3.5" /> {enviando ? 'Enviando…' : 'Enviar SMS'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
